import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { formatDistanceToNow, format } from 'date-fns';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
}

export function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

export function formatPercent(n: number, decimals = 0): string {
  return n.toFixed(decimals) + '%';
}

export function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

export function parseServerDate(dateStr: string): Date {
  const raw = String(dateStr || '').trim();
  if (!raw) {
    return new Date(NaN);
  }
  if (/^\d+$/.test(raw)) {
    return new Date(Number(raw));
  }
  const iso = raw.includes('T') ? raw : raw.replace(' ', 'T');
  if (/Z|[+-]\d{2}:?\d{2}$/.test(iso)) {
    return new Date(iso);
  }
  return new Date(iso.replace(/\.\d+$/, '') + 'Z');
}

export function timeAgo(dateStr: string): string {
  try {
    return formatDistanceToNow(parseServerDate(dateStr), { addSuffix: true });
  } catch {
    return dateStr;
  }
}

export function formatDate(dateStr: string, fmt = 'MMM d, yyyy HH:mm'): string {
  try {
    return format(parseServerDate(dateStr), fmt);
  } catch {
    return dateStr;
  }
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

export function getStatusColor(status: string): string {
  const map: Record<string, string> = {
    online: 'var(--status-online)',
    offline: 'var(--status-offline)',
    warning: 'var(--status-warning)',
    critical: 'var(--status-critical)',
    active: 'var(--status-online)',
    inactive: 'var(--status-offline)',
    running: 'var(--status-online)',
    stopped: 'var(--status-offline)',
    completed: 'var(--status-online)',
    failed: 'var(--status-critical)',
    pending: 'var(--status-warning)',
    queued: 'var(--text-muted)',
    cancelled: 'var(--text-muted)',
    open: 'var(--status-critical)',
    acknowledged: 'var(--status-warning)',
    resolved: 'var(--status-online)',
    escalated: 'var(--status-critical)',
    low: 'var(--status-info)',
    medium: 'var(--status-warning)',
    high: 'var(--status-warning)',
    compliant: 'var(--status-online)',
    missing_updates: 'var(--status-warning)',
    failed_updates: 'var(--status-critical)',
  };
  return map[status] || 'var(--text-muted)';
}

export function debounce<T extends (...args: any[]) => void>(fn: T, ms: number): T {
  let timer: ReturnType<typeof setTimeout>;
  return ((...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  }) as T;
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function randomId(prefix = ''): string {
  return prefix + Math.random().toString(36).substring(2, 11);
}

export function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function pick<T>(arr: readonly T[] | T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
